import { ref, readonly, computed } from 'vue';
import axios from 'axios';

const positions = ref({
    staking: [],
    lending: [],
    liquidity: [],
    earn: [],
    predictions: [],
});
const totalValue = ref(0);
const loading = ref(false);
const error = ref(null);
const lastFetched = ref(null);

/**
 * Aggregates DeFi positions across protocols for connected wallets.
 * Uses backend cache when available, with SDK as future extension.
 */
export function useWalletPositions() {
    function getWalletAddress() {
        return window.wallet?.publicKey?.value?.toBase58() || null;
    }

    async function fetchPositions() {
        const address = getWalletAddress();
        if (!address) {
            resetPositions();
            return positions.value;
        }

        try {
            loading.value = true;
            error.value = null;

            const { data } = await axios.get(`/api/portfolio/${address}`);
            if (data?.data) {
                positions.value = {
                    staking: data.data.staking || [],
                    lending: data.data.lending || [],
                    liquidity: data.data.liquidity || [],
                    earn: data.data.earn || [],
                    predictions: data.data.predictions || [],
                };
                totalValue.value = data.data.totalValue || 0;
            }

            lastFetched.value = Date.now();
            return positions.value;
        } catch (e) {
            error.value = e;
            console.error('[useWalletPositions] Failed to fetch positions:', e);
            return positions.value;
        } finally {
            loading.value = false;
        }
    }

    function resetPositions() {
        positions.value = {
            staking: [],
            lending: [],
            liquidity: [],
            earn: [],
            predictions: [],
        };
        totalValue.value = 0;
        lastFetched.value = null;
    }

    const hasPositions = computed(() =>
        Object.values(positions.value).some(arr => arr.length > 0)
    );

    const positionsByCategory = computed(() => {
        const cats = {};
        for (const [category, items] of Object.entries(positions.value)) {
            if (items.length > 0) {
                cats[category] = items;
            }
        }
        return cats;
    });

    return {
        fetchPositions,
        resetPositions,
        positions: readonly(positions),
        totalValue: readonly(totalValue),
        hasPositions,
        positionsByCategory,
        loading: readonly(loading),
        error: readonly(error),
        lastFetched: readonly(lastFetched),
    };
}
